import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { useTheme } from '@/contexts/ThemeContext';

import type { BarChartOptions } from '../types';
import {
  categoryAxisTickRenderer,
  MAX_UNTHINNED_CATEGORIES,
} from './CategoryAxisTick';
import {
  isDimmed,
  makeCellKeyDownHandler,
  makeSeriesClickHandler,
  markOpacity,
  sourceRowIndexOf,
} from './chartSelection';
import { CartesianLegend, ChartCartesianGrid, ChartTooltip } from './parts';
import {
  AXIS_TICK_FONT_SIZE,
  CARTESIAN_CHART_MARGIN,
  type CartesianChartCaseProps,
  categoryAxisHeight,
  CHART_SERIES_ANIMATION,
  DEFAULT_BAR_OPTIONS,
  formatAxisValue,
  mapScale,
  resolveCssColor,
  valueAxisProps,
  xAxisLabelProps,
} from './shared';

/** Hoisted: avoids creating a new object on every render when interactive. */
const POINTER_CURSOR_STYLE = { cursor: 'pointer' } as const;

/**
 * Width for the left axis when horizontal bars put *categories* on it. The
 * value axis auto-sizes to its ticks, but a category axis must not: one long
 * deal name would push the plot off the tile. Matches recharts' own default,
 * which is what this axis used before auto-sizing existed.
 */
const HORIZONTAL_CATEGORY_AXIS_WIDTH = 60;

interface BarChartCaseProps extends CartesianChartCaseProps {
  /** Raw category-axis label default, used when bar orientation swaps axes. */
  defaultCategoryAxisLabel?: string;
  /** Raw value-axis label default, used when bar orientation swaps axes. */
  defaultValueAxisLabel?: string;
  /**
   * One resolved palette colour per `chartData` row, for a single-measure
   * chart whose bars stand for named categories rather than one series.
   * Omitted (and the measure's own colour used for every bar) for a
   * multi-measure chart or a date-granularity axis — see `ChartRendererCore`.
   */
  categoryColors?: string[];
}

/**
 * BarChartCase component - extracted to keep chart-type rendering logic out
 * of the switch statement in ChartRendererCore (mirrors PieChartCase above).
 */
export function BarChartCase({
  chartData,
  config,
  columnTitles,
  height,
  legendAlign,
  tooltipFormatter,
  seriesKeys,
  seriesColors,
  categoryColors,
  axisMeta,
  categoryBottomLabel,
  valueAxisTitle,
  defaultCategoryAxisLabel,
  defaultValueAxisLabel,
  showXAxisLabels = true,
  yAxisWidth,
  renderLegend = true,
  syncId,
  onDatapointSelect,
  activeSelection,
}: BarChartCaseProps) {
  const barOptions =
    (config.chartOptions as BarChartOptions) || DEFAULT_BAR_OPTIONS;

  // Compute tick formatters to avoid nested ternaries
  let xAxisTickFormatter: ((value: number) => string) | undefined;
  if (barOptions.orientation === 'horizontal') {
    if (config.axes?.xAxis?.unit) {
      xAxisTickFormatter = value => formatAxisValue(value, config.axes?.xAxis);
    } else {
      xAxisTickFormatter = axisMeta.autoTickFormatter;
    }
  }

  let yAxisTickFormatter: ((value: number) => string) | undefined;
  if (barOptions.orientation === 'vertical') {
    if (config.axes?.yAxisLeft?.unit) {
      yAxisTickFormatter = value =>
        formatAxisValue(value, config.axes?.yAxisLeft);
    } else {
      yAxisTickFormatter = axisMeta.autoTickFormatter;
    }
  }

  // Horizontal bars swap which physical axis carries which title: the
  // bottom axis renders the value, the left axis the categories.
  const barBottomLabel =
    barOptions.orientation === 'horizontal'
      ? config.axes?.xAxis.label || defaultValueAxisLabel
      : categoryBottomLabel;
  const barLeftLabel =
    barOptions.orientation === 'horizontal'
      ? config.axes?.yAxisLeft.label || defaultCategoryAxisLabel
      : valueAxisTitle;
  const isHorizontal = barOptions.orientation === 'horizontal';

  // Vertical bars put a handful of named categories on the x-axis, and
  // recharts drops any whose single-line width overruns its band — on a
  // half-width tile that silently deleted the first stage of "Pipeline by
  // stage". Render every tick and wrap the long ones instead, but only while
  // there are few enough categories for that to fit; past that, recharts'
  // thinning is the better answer.
  const wrapCategoryTicks =
    !isHorizontal &&
    showXAxisLabels &&
    !config.axes?.xAxis.rotation &&
    chartData.length > 0 &&
    chartData.length <= MAX_UNTHINNED_CATEGORIES;
  // Only a label with a space can become two lines, so only then does the
  // axis need a second line's worth of band.
  const wrappedTickLines =
    wrapCategoryTicks &&
    chartData.some(row => typeof row.x === 'string' && /\s/.test(row.x))
      ? 1
      : 0;
  // Height is explicit regardless of showXAxisLabels, so hidden-label panels
  // reserve the same band a labeled one would (keeps plot areas aligned).
  const xAxisHeight = categoryAxisHeight({
    hasTitle: Boolean(barBottomLabel),
    extraLines: wrappedTickLines,
  });

  const interactive = Boolean(onDatapointSelect);

  // Subscribe to the theme so a toggle re-renders this chart; the tick `fill`
  // is a resolved hex (an SVG attribute, not a live var()), so without a
  // re-render it would keep the old theme's color. Resolving the color in
  // render keys the memo on the value itself: the object reference stays stable
  // when the theme is unchanged and updates when the DOM's data-theme flips.
  useTheme();
  const tickColor = resolveCssColor('--color-text-secondary');
  const xAxisTick = useMemo(() => {
    if (!showXAxisLabels) {
      return false;
    }
    if (wrapCategoryTicks) {
      return categoryAxisTickRenderer({
        fill: tickColor,
        fontSize: AXIS_TICK_FONT_SIZE,
      });
    }
    return { fill: tickColor, fontSize: AXIS_TICK_FONT_SIZE };
  }, [showXAxisLabels, wrapCategoryTicks, tickColor]);

  return (
    <ResponsiveContainer width='100%' height={height}>
      <BarChart
        data={chartData}
        layout={
          barOptions.orientation === 'horizontal' ? 'vertical' : 'horizontal'
        }
        // Recharts' default band sizing leaves sliver-thin bars adrift in
        // whitespace when there are few categories (or few series in a
        // wide pane). Tighten the category gap so bars carry visual
        // weight, and cap the width so a one-category chart doesn't
        // become a single monolith.
        barCategoryGap='20%'
        margin={CARTESIAN_CHART_MARGIN}
        syncId={syncId}
      >
        <ChartCartesianGrid show={config.axes?.xAxis.showGrid} />
        <XAxis
          dataKey={isHorizontal ? undefined : 'x'}
          type={isHorizontal ? 'number' : 'category'}
          hide={!config.axes?.xAxis.show}
          axisLine={false}
          tickLine={false}
          tick={xAxisTick}
          height={xAxisHeight}
          // Recharts thins ticks by measuring each label as one line, which
          // is exactly what the wrapping renderer exists to avoid — so when
          // it is in play, every category gets its tick.
          interval={wrapCategoryTicks ? 0 : undefined}
          label={showXAxisLabels ? xAxisLabelProps(barBottomLabel) : undefined}
          angle={config.axes?.xAxis.rotation}
          tickFormatter={xAxisTickFormatter}
          // Whole-quantity data (counts, sums of integers) skips
          // fractional ticks; decimal measures (averages) keep them.
          allowDecimals={!axisMeta.allIntegerValues}
          ticks={isHorizontal ? axisMeta.smallIntegerTicks : undefined}
        />
        <YAxis
          dataKey={isHorizontal ? 'x' : undefined}
          type={isHorizontal ? 'category' : 'number'}
          {...valueAxisProps({
            axisMeta,
            axisConfig: config.axes?.yAxisLeft,
            label: barLeftLabel,
            // Horizontal bars put categories on this axis, where auto-sizing
            // to the longest name would eat the plot — pin it instead.
            width:
              yAxisWidth ??
              (isHorizontal ? HORIZONTAL_CATEGORY_AXIS_WIDTH : undefined),
            domainMin: axisMeta.defaultValueAxisMin,
            scale: mapScale(config.axes?.yAxisLeft.scale || 'linear'),
            tickFormatter: yAxisTickFormatter,
            ticks: isHorizontal ? undefined : axisMeta.smallIntegerTicks,
          })}
        />
        <ChartTooltip
          show={config.tooltip.show}
          formatter={tooltipFormatter}
          cursor={{
            fill: resolveCssColor('--color-bg-secondary') || 'rgba(0,0,0,0.03)',
          }}
        />
        <CartesianLegend
          show={config.legend.show && renderLegend}
          legend={config.legend}
          legendAlign={legendAlign}
        />
        {seriesKeys.map(key => (
          <Bar
            key={key}
            dataKey={key}
            name={columnTitles[key] || key}
            fill={seriesColors[key]}
            stackId={barOptions.stacking !== 'none' ? 'stack' : undefined}
            radius={[6, 6, 0, 0]}
            maxBarSize={48}
            onClick={makeSeriesClickHandler(key, onDatapointSelect)}
            style={interactive ? POINTER_CURSOR_STYLE : undefined}
            {...CHART_SERIES_ANIMATION}
          >
            {/* Cells carry the per-bar treatment: the selection dimming, and
                (for a one-measure categorical chart) the category's own
                colour. A read-only surface still needs them for the latter,
                which is why this no longer keys off `interactive` alone. */}
            {interactive || categoryColors
              ? chartData.map((datum, cellIndex) => {
                  const rowIndex = sourceRowIndexOf(datum);
                  let dimmed = false;
                  if (
                    activeSelection !== null &&
                    activeSelection !== undefined
                  ) {
                    dimmed =
                      rowIndex !== null
                        ? isDimmed(activeSelection, rowIndex, key)
                        : true;
                  }
                  const xLabel =
                    typeof datum['x'] === 'string'
                      ? datum['x']
                      : String(cellIndex);
                  const cellKey =
                    rowIndex !== null ? `r${rowIndex}` : `o${cellIndex}`;
                  const interactiveCell = interactive && rowIndex !== null;
                  return (
                    <Cell
                      key={cellKey}
                      // Always explicit: a Cell's props are merged over the
                      // Bar's, so an undefined `fill` here would erase it.
                      fill={categoryColors?.[cellIndex] ?? seriesColors[key]}
                      fillOpacity={markOpacity(dimmed)}
                      role={interactiveCell ? 'button' : undefined}
                      tabIndex={interactiveCell ? 0 : undefined}
                      aria-label={
                        interactiveCell
                          ? `${xLabel} — ${columnTitles[key] || key}`
                          : undefined
                      }
                      onKeyDown={
                        interactive
                          ? makeCellKeyDownHandler(
                              datum,
                              key,
                              onDatapointSelect
                            )
                          : undefined
                      }
                    />
                  );
                })
              : null}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
